import { ReactNode } from "react";
import clsx from "clsx";

import type { AppointmentSummaryProps } from "./AppointmentSummary";

import { EmployeeSummary } from "~/components/Appointment/EmployeeSummary";
import { MeetingTypeSummary } from "~/components/Appointment/MeetingTypeSummary";
import { SubjectSummary } from "~/components/Appointment/SubjectSummary";
import { CalendarIcon } from "~/components/Icons/CalendarIcon";
import { OfficeIcon } from "~/components/Icons/OfficeIcon";
import { Map, useMarkers } from "~/components/Map";
import { Text } from "~/components/Text";
import { useTimeZone } from "~/utils/date";
import { getFullName } from "~/utils/getFullName";
import { useLocalisedDate, useLocalisedTimePeriod } from "~/utils/timetable.utils";

export function DesktopAppointmentSummary({
  employee,
  location,
  meetingType,
  office,
  possibility,
  showEmployeePlaceholder,
  subject,
}: AppointmentSummaryProps) {
  const geolocation = location?.geolocation;
  const markers = useMarkers(geolocation);
  const showMap = meetingType === "on_location" && geolocation;

  return (
    <div className="flex flex-col gap-md" data-component="AppointmentSummary">
      <EmployeeSummary {...employee} showPlaceholder={showEmployeePlaceholder} />

      <SummaryCard isEmpty={!subject?.title && !meetingType && !office?.title}>
        {showMap && <Map className="h-[175px] rounded-tr-3xl" markers={markers} zoom={15} />}

        <div className="divide-y divide-gray-200 px-md py-sm">
          {subject?.title && (
            <SubjectSummary
              className="py-sm"
              end={possibility?.end}
              externalId={subject.externalId}
              start={possibility?.start}
              title={subject.title}
            />
          )}

          {meetingType && (
            <MeetingTypeSummary
              className="py-sm"
              employeeName={getFullName(employee)}
              locationAddress={location?.address ?? null}
              meetingType={meetingType}
              office={office}
            />
          )}

          {/* For office meeting types, the information is merged in MeetingTypeSummary */}
          {meetingType !== "office" && office && (
            <OfficeSummary className="py-sm" office={office} />
          )}

          {possibility?.start && possibility?.end && (
            <div className="py-sm">
              <TimetableSummaryDesktop
                end={possibility.end}
                officeTimeZone={office?.timeZone ?? null}
                start={possibility.start}
              />
            </div>
          )}
        </div>
      </SummaryCard>
    </div>
  );
}

interface SummaryCardProps {
  children: ReactNode;
  isEmpty: boolean;
}

function SummaryCard({ children, isEmpty }: SummaryCardProps) {
  return (
    <div
      className={clsx(
        "ring-1 ring-inset ring-gray-200",
        "rounded-2xl rounded-tl-none rounded-tr-3xl",
        "bg-white",
        "shadow-sm",
      )}
      data-component="SummaryCard"
    >
      {isEmpty ? (
        <div
          className="p-md text-center text-sm italic text-gray-400 lg:text-base"
          data-component="SummaryCardPlaceholder"
        >
          <Text i18nKey="summary.empty" />
        </div>
      ) : (
        children
      )}
    </div>
  );
}

interface TimetableSummaryProps {
  start: string;
  end: string;
  officeTimeZone: string | null;
  className?: string;
}

function TimetableSummaryDesktop({ className, end, officeTimeZone, start }: TimetableSummaryProps) {
  const timeZone = useTimeZone({ officeTimeZone });
  // const defaultTimeZone = useDefaultTimeZone({ officeTimeZone });

  const fullDate = useLocalisedDate({ start, timeZone });
  // const defaultFullDate = useLocalisedDate({ start, timeZone: defaultTimeZone });

  const date = useLocalisedDate({ start, timeZone, dateStyle: "long" });
  // const defaultDate = useLocalisedDate({ start, timeZone: defaultTimeZone, dateStyle: "long" });

  const time = useLocalisedTimePeriod({ start, end, timeZone });
  // const defaultTime = useLocalisedTimePeriod({ start, end, timeZone: defaultTimeZone });

  if (!date || !time) {
    return null;
  }

  return (
    <div className={clsx("grid gap-3xs", className)} data-component="TimetableSummaryDesktop">
      <div className="text-sm font-semibold">
        <Text i18nKey="util.date" />
      </div>
      <div className="flex items-start gap-xs">
        <CalendarIcon className="h-[20px] w-[20px] text-brand" />
        <div
          className="text-sm font-normal text-gray-500"
          data-component="TimetableSummaryDateTimeContainer"
        >
          <div className="hidden lg:block" data-component="TimetableSummaryDate">
            {fullDate}
            {/* {fullDate !== defaultFullDate && (
              <span className="px-sm text-warning">Default {defaultFullDate}</span>
            )} */}
          </div>
          <div className="block lg:hidden" data-component="TimetableSummaryDate">
            {date}
            {/* {date !== defaultDate && (
              <span className="px-sm text-warning">Default {defaultDate}</span>
            )} */}
          </div>
          <div className="flex flex-wrap gap-x-xs" data-component="TimetableSummaryTime">
            <span>{time}</span>
            {/* {time !== defaultTime && (
              <span className="px-sm text-warning">Default {defaultTime}</span>
            )} */}
            <span className="text-sm italic" data-component="TimetableSummaryTimeZone">
              {timeZone}
            </span>
            {/* {timeZone !== defaultTimeZone && <span className="text-sm italic text-warning" data-component="TimetableSummaryTimeZone">
                Default: {defaultTimeZone}
              </span>} */}
          </div>
        </div>
      </div>
    </div>
  );
}

export function OfficeSummary({
  className,
  office,
}: {
  office: AppointmentSummaryProps["office"];
  className?: string;
}) {
  if (!office) {
    return null;
  }

  return (
    <div
      className={clsx("grid gap-3xs", className)}
      data-component="OfficeSummary"
      data-external-id={office.externalId}
    >
      <span className="text-sm font-semibold">
        <Text i18nKey={office.isVirtual ? "summary.contact_center" : "summary.office_label"} />
      </span>

      <div className="flex items-start gap-xs">
        <div className="flex h-[20px] w-[20px] justify-center text-brand">
          <OfficeIcon outline />
        </div>

        <div className="text-sm font-normal text-gray-500" data-component="OfficeSummaryTitle">
          {office.title}
        </div>
      </div>
    </div>
  );
}
